import { usePutQuestionMutation } from "@api/quiz";
import { useModal } from "@components/Modal";
import {
  PencilSquareIcon,
  PlusCircleIcon,
  TrashIcon,
} from "@heroicons/react/16/solid";
import { useToastEffect } from "@hooks/hooks";
import { QuestionDTO, UpdateQuestionDTO } from "@onetutor/shared/dto";
import { QuestionDifficulty, QuestionType } from "@onetutor/shared/type";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { v4 as uuidv4 } from "uuid";

interface EditQuestionModalProps {
  question: QuestionDTO;
  setShouldFetch: React.Dispatch<React.SetStateAction<boolean>>;
  setQuestionsListParent?: React.Dispatch<React.SetStateAction<QuestionDTO[]>>;
}

export const EditQuestionModal = ({
  question,
  setShouldFetch,
  setQuestionsListParent,
}: EditQuestionModalProps) => {
  const { t } = useTranslation("quizManagementPage");
  const [passedQuestion, setPassedQuestion] = useState<QuestionDTO>(question);
  const [putQuestion, { isError, isSuccess, error }] = usePutQuestionMutation();

  const { Modal, openModal } = useModal();

  const { courseId } = useParams();
  if (!courseId) {
    throw new Error("Course ID is required");
  }

  const questionTextareaId = useId();
  const tipTextareaId = useId();
  const difficultySelectId = useId();

  useToastEffect({
    isError,
    isSuccess,
    successMessage: t("editQuestionModal.successEditQuestion", { ns: "toast" }),
    error,
  });

  useEffect(() => {
    setPassedQuestion(question);
  }, [question]);

  const handleSubmit = async () => {
    const questionBody: UpdateQuestionDTO = {
      answers: passedQuestion.answers.map((answer) => ({
        answer: answer.answer,
        isCorrect: answer.isCorrect,
      })),
      question: passedQuestion.question,
      tip: passedQuestion.tip,
      type: passedQuestion.type,
      topic: passedQuestion.topic,
      isPublished: passedQuestion.isPublished,
      difficulty: passedQuestion.difficulty,
    };

    const patchedQuestion = await putQuestion({
      courseId,
      questionId: question.id,
      body: questionBody,
    });

    if (patchedQuestion && setQuestionsListParent) {
      setQuestionsListParent((prev) => {
        return prev.map((q) => {
          if (q.id === question.id && patchedQuestion.data) {
            return patchedQuestion.data;
          }
          return q;
        });
      });
    }
    setShouldFetch(false);
  };

  return (
    <>
      <PencilSquareIcon
        className="ml-sm h-6 w-6 cursor-pointer"
        onClick={openModal}
      />

      <Modal
        title={t("onlyEditMask.editQuestion")}
        onConfirmDisabled={
          JSON.stringify(question) === JSON.stringify(passedQuestion)
        }
        onConfirm={handleSubmit}
        onConfirmLabel={t("save", { ns: "commons" })}
        isWider
      >
        <form className="flex flex-col gap-2">
          <label htmlFor={questionTextareaId}>
            <div className="font-semibold text-lg">
              {t("onlyEditMask.question")}
            </div>
          </label>
          <textarea
            id={questionTextareaId}
            value={passedQuestion.question}
            className="textarea h-auto w-[90%] resize-none"
            required
            onInput={(e) => {
              e.currentTarget.style.height = "auto"; // Reset the height
              e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`; // Set height based on content
            }}
            onChange={(e) => {
              setPassedQuestion((prev) => ({
                ...prev,
                question: e.target.value,
              }));
            }}
          />

          <label htmlFor={tipTextareaId}>
            <div className="font-semibold text-lg">{t("tip")}</div>
          </label>
          <textarea
            id={tipTextareaId}
            className="textarea h-auto w-[90%] resize-none"
            required
            value={passedQuestion.tip ?? ""}
            onInput={(e) => {
              e.currentTarget.style.height = "auto"; // Reset the height
              e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
            }}
            onChange={(e) => {
              setPassedQuestion((prev) => ({
                ...prev,
                tip: e.target.value,
              }));
            }}
          />

          <label htmlFor={difficultySelectId}>
            <div className="font-semibold text-lg">{t("difficulty")}</div>
          </label>
          <select
            id={difficultySelectId}
            className="select w-full max-w-xs"
            value={passedQuestion.difficulty}
            onChange={(e) => {
              setPassedQuestion((prev) => ({
                ...prev,
                difficulty: e.target.value as QuestionDifficulty,
              }));
            }}
          >
            {[...new Set(Object.values(QuestionDifficulty))].map((value) => (
              <option key={value} value={value}>
                {t(`difficultyOptions.${value}`)}
              </option>
            ))}
          </select>

          <label htmlFor="answer" className="flex items-center justify-start">
            <span className="font-semibold text-lg">
              {question.type === QuestionType.MULTIPLE_CHOICE
                ? t("onlyEditMask.answersMC")
                : t("correctAnswerFreeText")}
            </span>
            {question.type === QuestionType.MULTIPLE_CHOICE && (
              <button
                className="btn btn-sm btn-circle btn-ghost ml-2"
                type="button"
                onClick={() => {
                  setPassedQuestion((prev) => ({
                    ...prev,
                    answers: [
                      ...prev.answers,
                      {
                        id: uuidv4(),
                        answer: "",
                        isCorrect: false,
                      },
                    ],
                  }));
                }}
              >
                <PlusCircleIcon className="h-7 w-7 cursor-pointer" />
              </button>
            )}
          </label>

          {passedQuestion.answers.map((answer, index) => (
            <div
              key={answer.id}
              className="flex items-center justify-between gap-2"
            >
              <textarea
                className="textarea h-auto w-[90%] resize-none"
                required
                value={answer.answer}
                onInput={(e) => {
                  e.currentTarget.style.height = "auto";
                  e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                }}
                onChange={(e) => {
                  const newAnswers = [...passedQuestion.answers];
                  newAnswers[index] = {
                    ...newAnswers[index],
                    answer: e.target.value,
                  };
                  setPassedQuestion((prev) => ({
                    ...prev,
                    answers: newAnswers,
                  }));
                }}
              />
              {question.type === QuestionType.MULTIPLE_CHOICE && (
                <>
                  <div
                    className="tooltip tooltip-left tooltip-primary"
                    data-tip={t("onlyEditMask.tooltipCorrectAnswers")}
                  >
                    <input
                      type="checkbox"
                      checked={answer.isCorrect}
                      onChange={(e) => {
                        const newAnswers = [...passedQuestion.answers];
                        newAnswers[index] = {
                          ...newAnswers[index],
                          isCorrect: e.target.checked,
                        };
                        setPassedQuestion((prev) => ({
                          ...prev,
                          answers: newAnswers,
                        }));
                      }}
                      className={"checkbox"}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-circle btn-ghost mr-2"
                    onClick={() => {
                      const newAnswers = [...passedQuestion.answers];
                      newAnswers.splice(index, 1);
                      setPassedQuestion((prev) => ({
                        ...prev,
                        answers: newAnswers,
                      }));
                    }}
                  >
                    <TrashIcon className="h-6 w-6" />
                  </button>
                </>
              )}
            </div>
          ))}
        </form>
      </Modal>
    </>
  );
};
